import { ArrowUpRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  AI_APP_LISTINGS,
  AiApp,
} from '@/Domain/components/Integration/AccountMcpConnect/accountMcp.const';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import { AccountMcpActionSource, PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { isCredentialBearingNext, readNextParamFromSearch } from '@/Domain/utils/safeNextPath';

const MCP_AUTHORIZE_PATH = '/v1/api/mcp/oauth/authorize';

const ENTRY_COPY_KEYS = {
  chatgpt: { labelKey: 'chatgpt_button', tooltipKey: 'chatgpt_tooltip' },
  claude: { labelKey: 'claude_button', tooltipKey: 'claude_tooltip' },
} as const satisfies Record<AiApp, { labelKey: string; tooltipKey: string }>;

export const AiAppListingButton = ({
  app,
  source,
}: {
  app: AiApp;
  source: AccountMcpActionSource;
}) => {
  const { t } = useTranslation('entry');
  const { capture } = usePostHogAnalytics();
  const { search } = useLocation();
  const next = readNextParamFromSearch(search);
  const { href, Icon, client } = AI_APP_LISTINGS[app];
  const { labelKey, tooltipKey } = ENTRY_COPY_KEYS[app];

  // A visitor bounced here by the MCP authorize endpoint is already mid an AI app's connect flow; the link would loop them back out of it.
  if (next?.startsWith(MCP_AUTHORIZE_PATH)) {
    return null;
  }

  // A visitor mid a ceremony link (e.g. link-channel) still gets the button, but the click must
  // stay silent -- posthog-js autocaptures $current_url, which would stamp the bearer into PostHog.
  const suppressAnalytics = isCredentialBearingNext(next);

  return (
    // EntryLayout's provider wraps only the theme toggler, so this brings its own.
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button asChild variant="secondary" size="lg" className="w-full gap-3 font-medium">
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              // Explicit capture() is only half the leak: autocapture, heatmaps and dead-click
              // capture are all enabled globally and fire off this element's own click listener,
              // each stamping $current_url regardless of what our onClick does. ph-no-capture is
              // the one thing that opts the element itself out of all three (see the identical
              // case in SupportWhatsAppConnect.tsx).
              className={suppressAnalytics ? 'ph-no-capture' : undefined}
              onClick={() => {
                if (!suppressAnalytics) {
                  capture(PostHogEventEnum.AccountMcpActionClicked, {
                    client,
                    action: 'open_app',
                    source,
                  });
                }
              }}
              data-testid={`entry-${app}-app`}
            >
              <Icon className="size-4" />
              <span className="flex items-center gap-1.5">
                {t(labelKey)}
                <ArrowUpRight aria-hidden="true" />
              </span>
              <span className="sr-only">{t('opens_in_new_tab')}</span>
            </a>
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">{t(tooltipKey)}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
